import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle, Eye, Clock, AlertCircle, Users, ListChecks, Ban } from 'lucide-react';
import { api, openAuthedFile } from '@/lib/api';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ui/confirm-dialog';
import { formatWIB } from '@/lib/datetime';

export default function AdminVervalGTKPage() {
  const [requests, setRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]); // unfiltered, for the stats overview
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchName, setSearchName] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      // Fetch GTK requests (guru + tenaga_kependidikan) with optional status filter
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/verval-requests', { params });
      // Filter on frontend for GTK
      const gtkRequests = data.filter(r => r.user_type === 'guru' || r.user_type === 'tenaga_kependidikan');
      setRequests(gtkRequests);
      if (statusFilter === 'all') {
        setAllRequests(gtkRequests);
      } else {
        const { data: all } = await api.get('/verval-requests', { params: {} });
        setAllRequests(all.filter((r) => r.user_type === 'guru' || r.user_type === 'tenaga_kependidikan'));
      }
    } catch (e) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [statusFilter]);

  const openDetail = (req) => {
    setSelectedRequest(req);
    setAdminNotes(req.admin_notes || '');
    setDetailOpen(true);
  };

  const handleApprove = async () => {
    if (!(await confirmDialog('Setujui perubahan data ini?'))) return;
    setActionLoading(true);
    try {
      await api.post(`/verval-requests/${selectedRequest.id}/approve`, { admin_notes: adminNotes });
      toast.success('Perubahan data disetujui');
      setDetailOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!adminNotes.trim()) {
      toast.error('Catatan penolakan wajib diisi');
      return;
    }
    if (!(await confirmDialog('Tolak perubahan data ini? User akan menerima catatan penolakan.'))) return;
    setActionLoading(true);
    try {
      await api.post(`/verval-requests/${selectedRequest.id}/reject`, { admin_notes: adminNotes });
      toast.success('Perubahan data ditolak');
      setDetailOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal reject');
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'pending') return <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" /> Menunggu</Badge>;
    if (status === 'approved') return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="h-3 w-3 mr-1" /> Disetujui</Badge>;
    if (status === 'rejected') return <Badge className="bg-rose-100 text-rose-800"><XCircle className="h-3 w-3 mr-1" /> Ditolak</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
  };

  const userTypeBadge = (userType) => {
    if (userType === 'guru') return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Guru</Badge>;
    if (userType === 'tenaga_kependidikan') return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Tendik</Badge>;
    return <Badge variant="secondary">{userType}</Badge>;
  };

  const renderComparison = () => {
    if (!selectedRequest) return null;
    const { old_data, new_data } = selectedRequest;

    // Field labels yang user-friendly
    const fieldLabels = {
      full_name: 'Nama Lengkap',
      nama_tanpa_gelar: 'Nama Tanpa Gelar',
      gelar_depan: 'Gelar Depan',
      gelar_belakang: 'Gelar Belakang',
      email: 'Email',
      phone: 'Nomor Telepon',
      nip_nuptk: 'NIP/NUPTK',
      peg_id: 'Peg ID',
      npk: 'NPK',
      nrg: 'NRG',
      address: 'Alamat',
      gender: 'Jenis Kelamin',
      birth_place: 'Tempat Lahir',
      birth_date: 'Tanggal Lahir',
      status_tempat_tinggal: 'Status Tempat Tinggal',
      provinsi: 'Provinsi',
      kab_kota: 'Kabupaten/Kota',
      kecamatan: 'Kecamatan',
      kelurahan: 'Kelurahan/Desa',
      kode_wilayah: 'Kode Wilayah',
      rt: 'RT',
      rw: 'RW',
      kode_pos: 'Kode Pos',
      // Add more fields as needed
    };

    // Nilai daftar (riwayat, data anak), ya/tidak, dan berkas ditampilkan rapi, bukan objek mentah.
    const tampilNilai = (v) => {
      if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return <span className="text-slate-400 italic">Tidak ada data</span>;
      if (typeof v === 'boolean') return v ? 'Ya' : 'Tidak';
      if (Array.isArray(v)) {
        return (
          <span className="block space-y-0.5">
            {v.map((it, i) => <span key={i} className="block">{i + 1}. {typeof it === 'object' ? Object.values(it || {}).filter(Boolean).join(' · ') : String(it)}</span>)}
          </span>
        );
      }
      if (typeof v === 'object') return JSON.stringify(v);
      if (typeof v === 'string' && v.startsWith('/api/')) {
        return <button type="button" onClick={() => openAuthedFile(v)} className="text-blue-600 underline text-sm">Lihat berkas</button>;
      }
      return String(v);
    };

    // Cari field yang berubah (kosong/undefined/null dianggap sama), field nama & gelar ditampilkan paling atas.
    const kosong = (v) => v == null || v === '';
    const URUTAN_NAMA = ['gelar_depan', 'nama_tanpa_gelar', 'gelar_belakang', 'full_name',
      'status_tempat_tinggal', 'provinsi', 'kab_kota', 'kecamatan', 'kelurahan', 'rt', 'rw', 'kode_pos', 'kode_wilayah'];
    const changedFields = Object.keys(new_data)
      .filter((key) => !(kosong(new_data[key]) && kosong(old_data[key])) && JSON.stringify(new_data[key] ?? '') !== JSON.stringify(old_data[key] ?? ''))
      .sort((a, b) => {
        const ia = URUTAN_NAMA.indexOf(a); const ib = URUTAN_NAMA.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });

    if (changedFields.length === 0) {
      return <div className="text-center py-8 text-slate-500">Tidak ada perubahan data</div>;
    }

    return (
      <div className="space-y-4">
        {changedFields.map(field => (
          <div key={field} className="grid grid-cols-2 gap-4 border rounded-lg p-4">
            <div>
              <Label className="text-xs text-slate-600 mb-2 block">{fieldLabels[field] || field} - SEBELUM</Label>
              <div className="bg-rose-50 border border-rose-200 rounded p-3 min-h-[60px]">
                <p className="text-sm text-slate-900 break-words">{tampilNilai(old_data[field])}</p>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-2 block">{fieldLabels[field] || field} - SESUDAH</Label>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 min-h-[60px]">
                <p className="text-sm text-slate-900 font-semibold break-words">{tampilNilai(new_data[field])}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const stats = {
    total: allRequests.length,
    pending: allRequests.filter((r) => r.status === 'pending').length,
    approved: allRequests.filter((r) => r.status === 'approved').length,
    rejected: allRequests.filter((r) => r.status === 'rejected').length,
    cancelled: allRequests.filter((r) => r.status === 'cancelled').length,
  };
  const pendingCount = stats.pending;
  const filteredRequests = requests.filter((r) => {
    const keyword = searchName.trim().toLowerCase();
    if (!keyword) return true;
    const name = (r.submitted_by_name || '').toLowerCase();
    return name.includes(keyword);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">
            <Users className="h-3 w-3 mr-1" /> Verval Data GTK
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-bold">Verifikasi & Validasi Data GTK</h1>
          <p className="text-sm text-slate-600 mt-1">
            {filteredRequests.length} request ditampilkan • {pendingCount} menunggu review
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Semua Status</option>
            <option value="pending">Menunggu</option>
            <option value="approved">Disetujui</option>
            <option value="rejected">Ditolak</option>
            <option value="cancelled">Dibatalkan</option>
          </select>
          <Input
            placeholder="Cari nama..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="w-[220px]"
          />
          <Button onClick={refresh} variant="outline" disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBox icon={ListChecks} label="Total" value={stats.total} color="bg-slate-50 border-slate-200 text-slate-700" />
        <StatBox icon={Clock} label="Menunggu" value={stats.pending} color="bg-amber-50 border-amber-200 text-amber-700" />
        <StatBox icon={CheckCircle} label="Disetujui" value={stats.approved} color="bg-emerald-50 border-emerald-200 text-emerald-700" />
        <StatBox icon={XCircle} label="Ditolak" value={stats.rejected} color="bg-rose-50 border-rose-200 text-rose-700" />
        <StatBox icon={Ban} label="Dibatalkan" value={stats.cancelled} color="bg-slate-50 border-slate-200 text-slate-500" />
      </div>

      {pendingCount > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <p className="text-sm text-amber-900">
              Ada <strong>{pendingCount} request</strong> yang menunggu untuk direview.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tanggal Pengajuan</TableHead>
                  <TableHead>Nama</TableHead>
                  <TableHead>Jenis</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Direview Oleh</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">Memuat...</TableCell>
                  </TableRow>
                ) : filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada request verval</TableCell>
                  </TableRow>
                ) : filteredRequests.map(req => (
                  <TableRow key={req.id}>
                    <TableCell className="text-sm">
                      {formatWIB(req.created_at, 'Date', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell className="font-semibold">{req.submitted_by_name || req.user_id}</TableCell>
                    <TableCell>{userTypeBadge(req.user_type)}</TableCell>
                    <TableCell>{statusBadge(req.status)}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {req.reviewed_by_name || '-'}
                      {req.reviewed_at && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          {formatWIB(req.reviewed_at, 'Date', { day: '2-digit', month: 'short' })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(req)} className="gap-1">
                        <Eye className="h-3 w-3" /> Detail
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Comparison */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#006837]" />
              Verifikasi Perubahan Data GTK
            </DialogTitle>
            <DialogDescription>
              Periksa perubahan data di bawah ini. Data <strong>sebelum</strong> ditampilkan di sebelah kiri,
              data <strong>perubahan yang diajukan</strong> di sebelah kanan.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedRequest && (
              <>
                <div className="bg-slate-50 border rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-slate-600">Diajukan oleh:</span> <strong>{selectedRequest.submitted_by_name}</strong></div>
                    <div><span className="text-slate-600">Status:</span> {statusBadge(selectedRequest.status)}</div>
                    <div><span className="text-slate-600">Jenis:</span> {userTypeBadge(selectedRequest.user_type)}</div>
                    <div><span className="text-slate-600">Tanggal:</span> {formatWIB(selectedRequest.created_at, '')}</div>
                    {selectedRequest.reviewed_by_name && (
                      <div className="col-span-2"><span className="text-slate-600">Direview oleh:</span> {selectedRequest.reviewed_by_name}</div>
                    )}
                  </div>
                </div>

                {renderComparison()}

                {selectedRequest.status === 'pending' && (
                  <div>
                    <Label className="mb-2">Catatan Admin</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Tambahkan catatan (opsional untuk approve, wajib untuk reject)"
                      rows={3}
                    />
                  </div>
                )}

                {selectedRequest.status !== 'pending' && selectedRequest.admin_notes && (
                  <div className="bg-slate-50 border rounded-lg p-3">
                    <Label className="text-xs text-slate-600 mb-1 block">Catatan Admin:</Label>
                    <p className="text-sm">{selectedRequest.admin_notes}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDetailOpen(false)} disabled={actionLoading}>
              Tutup
            </Button>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="text-rose-600 border-rose-300 hover:bg-rose-50"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Tolak
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Setujui
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="text-2xl font-extrabold tabular-nums mt-1">{value}</div>
    </div>
  );
}
